/**
 * Words written for a screen, as the pieces a person changes one at a time: each paragraph, a list's lead-in
 * line and each list line. A paragraph is the text between blank lines; a list line starts with "- ".
 * Changing one piece rewrites only its own paragraph, so every other paragraph keeps its exact text.
 */

/** A piece's place: paragraph `i`, and in a list the lead-in (`"head"`) or line `j`. */
export interface At {
  i: number;
  j?: number | "head";
}

const ITEM = /^\s*-\s+/;

/** The paragraphs, each with the separator that follows it, so untouched ones come back byte for byte. */
function split(text: string): { paras: string[]; seps: string[] } {
  const parts = String(text).split(/(\n[ \t]*\n\s*)/);
  const paras: string[] = [];
  const seps: string[] = [];
  for (let k = 0; k < parts.length; k += 2) {
    if (!parts[k].trim()) continue;
    paras.push(parts[k]);
    seps.push(parts[k + 1] ?? "");
  }
  return { paras, seps };
}

function join(paras: string[], seps: string[]): string {
  return paras.map((p, k) => (k < paras.length - 1 ? p + (seps[k] || "\n\n") : p)).join("");
}

/** The trimmed paragraphs, as the page draws them. */
export const paragraphs = (text: string): string[] => split(text).paras.map((p) => p.trim());

export const itemsOf = (p: string): string[] =>
  p
    .split("\n")
    .filter((l) => ITEM.test(l))
    .map((l) => l.replace(ITEM, "").trim());

export const headOf = (p: string): string =>
  p
    .split("\n")
    .filter((l) => !ITEM.test(l))
    .join(" ")
    .trim();

/** The words of one piece, as the person sees them in its edit box. */
export function pieceText(text: string, at: At): string {
  const p = paragraphs(text)[at.i] ?? "";
  if (at.j === undefined) return p;
  if (at.j === "head") return headOf(p);
  return itemsOf(p)[at.j] ?? "";
}

/** Every piece's words, to tell a piece the person wrote from one that was there. */
export function pieceTexts(text: string): Set<string> {
  const out = new Set<string>();
  for (const p of paragraphs(text)) {
    const its = itemsOf(p);
    if (!its.length) out.add(p);
    else {
      out.add(headOf(p));
      for (const it of its) out.add(it);
    }
  }
  return out;
}

const listPara = (head: string, items: string[]) => [head, ...items.map((x) => `- ${x}`)].filter((x) => x.trim()).join("\n");
const lines = (value: string) =>
  value
    .split(/\n+/)
    .map((l) => l.replace(ITEM, "").trim())
    .filter(Boolean);

/**
 * The words with one piece changed. Emptying a piece takes it out; a paragraph that gains blank lines becomes
 * several, and a list line that gains line breaks becomes several lines.
 */
export function setPiece(text: string, at: At, value: string): string {
  const { paras, seps } = split(text);
  const raw = paras[at.i];
  if (raw === undefined) return text;
  const v = value.trim();
  let next: string;
  if (at.j === undefined) next = v;
  else {
    const head = at.j === "head" ? v.replace(/\s*\n\s*/g, " ") : headOf(raw);
    const items = itemsOf(raw);
    if (typeof at.j === "number") items.splice(at.j, 1, ...lines(v));
    next = listPara(head, items);
  }
  if (!next) {
    paras.splice(at.i, 1);
    seps.splice(at.i, 1);
  } else paras[at.i] = next;
  return join(paras, seps);
}

/** The words with a new list line at line `j` of paragraph `i`. Nothing changes when the line is empty. */
export function addLine(text: string, i: number, j: number, value: string): string {
  const { paras, seps } = split(text);
  const raw = paras[i];
  const add = lines(value);
  if (raw === undefined || !add.length) return text;
  const items = itemsOf(raw);
  items.splice(j, 0, ...add);
  paras[i] = listPara(headOf(raw), items);
  return join(paras, seps);
}
